<template>
	<div class="ccs-logo ccs-logo-recommends"
		 :class="{ 'ccs-small': narrowContainer }"
		 :data-key="options.key">

		<div class="ccs-logo-navbar" :data-tab-count="tabs && tabs.length">
			<tabs v-if="activeTab" :items="tabs" @changed="changeTab" />
			<div v-if="options.headerText" class="ccs-logo-navbar-text">{{ options.headerText }}</div>
			<div class="ccs-logo-navbar-icon"></div>
		</div>

		<transition name="ccs-fade">
			<product-info :product="activeProduct"
						  :key="activeProduct.index"
						  :log-event="logProductEvent">
			</product-info>
		</transition>

		<div ref="carousel" class="ccs-logo-carousel">
			<slick ref="slick" :options="slickOptions" :slide-count="slideCount">
				<carousel-item v-for="product in filteredProducts"
							   :key="product.index"
							   :product="product"
							   :is-active="product == activeProduct"
							   @click="changeProduct" />
			</slick>
		</div>

		<div class="ccs-logo-footer" v-if="options.footerText">{{ options.footerText }}</div>
	</div>
</template>

<script>

	import tabs from "./tabs.vue";
	import productInfo from "./product-info.vue";
	import carouselItem from "./carousel-item.vue";

	import slick from "slick.vue";
	import ResizeSensor from "ResizeSensor";

	export default {
		components: { tabs, productInfo, slick, carouselItem },

		props: {
			tabs: Array,
			products: Array,
			options: Object
		},

		data() {
			const vue = this;

			return {
				allProducts: this.mapProducts(),
				activeTab: this.tabs && this.tabs[0],
				activeProduct: undefined,
				narrowContainer: undefined,
				slideCount: undefined,
				slickOptions: {
					infinite: false,
					draggable: false,
					// can't figure out how to pass slick wrapper to slick options before mount
					// someday this hack should be rewritten
					get appendArrows() {
						return vue.$refs.carousel;
					}
				}
			};
		},

		computed: {
			filteredProducts: function () {
				if (!this.activeTab) {
					return this.allProducts;
				}

				return this.allProducts.filter(x => x.category === this.activeTab.id);
			}
		},

		beforeMount() {
			this.activeProduct = this.filteredProducts[0];
		},

		mounted() {
			// trigger initial resize() when $el become visible (a hack to avoid setTimeout)
			var animationStart = "animationstart";
			if ("webkitAnimationName" in document.documentElement.style) {
				animationStart = 'webkitAnimationStart';
			} else if ("MozAnimationName" in document.documentElement.style) {
				animationStart = "mozanimationstart";
			}

			this.$el.addEventListener(animationStart, this.resize);

			new ResizeSensor(this.$el, this.resize);
		},

		methods: {
			mapProducts() {
				let priceExt = {};

				if (this.options.parsingPriceJs) {
					priceExt = {
						value: null,
						loading: false,
						requested: false,
						failed: false
					};
				}

				return this.products.map((data, index) => {
					const product = { index, ...data };
					product.price = { ...priceExt, ...product.price };

					if (this.tabs) {
						product.eventData.sourceTab =
							this.tabs
								// replace with "find" after getting rid of IE
								.filter(tab => tab.id === product.category)[0]
								.systemName;
					}

					return product;
				});
			},

			changeTab(tab) {
				if (this.activeTab === tab) {
					return;
				}

				this.activeTab = tab;
				this.activeProduct = this.filteredProducts[0];
				this.resize();
				this.logEvent("navigate-content", { type: "tab", id: tab.id, name: tab.systemName });
			},

			changeProduct(product) {
				if (product === this.activeProduct) {
					return;
				}

				this.activeProduct = product;
				this.logProductEvent(product, "navigate-content", "product");
			},

			resize() {
				const width = this.$el.offsetWidth;

				this.slideCount = this.options.maxSlideItems && this.filteredProducts.length >= this.options.maxSlideItems
					? this.options.maxSlideItems
					: Math.max(Math.floor(width / 160), 2);

				this.narrowContainer = width < 500;
			},

			logEvent(action, context) {
				if (context && typeof context === "object") {
					context = JSON.stringify(context);
				}

				const clientParams = {
					Action: action,
					ActionContext: context
				};

				ccs_cc_log.dispatchEvent(this.options.eventCall, clientParams);
			},

			logProductEvent(product, action, actionContextType, clientParams) {
				const context = Object.assign({}, product.eventData, { type: actionContextType }, clientParams);
				this.logEvent(action, context);
			},
		},

		watch: {
			filteredProducts() {
				this.$refs.slick.reInit();
			},

			activeProduct(product) {
				const parsingPriceJs = this.options.parsingPriceJs;
				const logoProgram = window.CcsLogoProgram;
				const price = product.price;

				if (!logoProgram || !parsingPriceJs || price.requested || price.loading) {
					return;
				}

				if (!price.url) {
					price.failed = price.requested = true;
					return;
				}

				price.loading = true;

				logoProgram.requestPrice(
					data => {
						price.loading = false;
						price.requested = true;

						if (data && data.result) {
							price.value = data.result.toString().trim();
						} else {
							price.failed = true;
						}
					},
					price.url,
					parsingPriceJs,
					this.options.eventCall);
			}
		},

		provide: function () {
			return {
				options: this.options
			}
		}
	}
</script>

<style scoped>
	.ccs-logo-recommends {
		animation: 0.1s ccs-visibility-check;
		transform: translate3d(0,0,0);
	}

	.ccs-logo-navbar {
		position: relative;
		display: flex;
	}

		.ccs-logo-navbar[data-tab-count] {
			border-bottom: 1px solid #555;
			padding: 0 5px;
		}

	.ccs-logo-footer {
		margin: 10px;
		font-size: 10px;
		color: #000000;
	}

	/* TRANSITION */
		.ccs-fade-enter-active, .ccs-fade-leave-active {
		transition: opacity .3s ease;
	}

	.ccs-fade-enter,
	.ccs-fade-leave-to {
		opacity: 0;
	}

	.ccs-fade-leave-active,
	.ccs-fade-leave-to {
		position: absolute;
		width: 100%;
	}

	/* LOGO HOVER */

	.ccs-logo-ls-popup .ccs-logo-recommends,
	body > [id^=logoTemplateEntry] > .ccs-logo-recommends {
		font-size: 14px;
	}

	.ccs-logo-ls-popup .ccs-logo-carousel {
		margin: 0 4px;
	}

	@keyframes ccs-visibility-check {
		0% {
			visibility: inherit;
		}
	}
</style>

<style lang="sass">
	@import "styles/slick";
	@import "styles/vendor";
</style>
